// Er sladdingen din trygg? — analyserer et bilde lokalt og forteller om
// sladdingen kan reverseres av AI. Ingen opplasting.
import React, { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Upload, ShieldCheck, ShieldAlert, ShieldX, Shield, Info, ArrowRight,
  Loader2, RotateCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { analyzeImageFull } from "../lib/blur-check";

export default function BlurSjekk() {
  const [image, setImage] = useState(null);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);

  const handleUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Kun bildefiler støttes");
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      setImage(img);
      setFileName(file.name);
      setResult(null);
      URL.revokeObjectURL(url);
      // Tegn til canvas + kjør analyse
      setTimeout(() => runAnalysis(img), 60);
    };
    img.src = url;
  };

  const runAnalysis = async (img) => {
    if (!canvasRef.current) return;
    setAnalyzing(true);
    try {
      const canvas = canvasRef.current;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      // La rendring gå gjennom først
      await new Promise((r) => setTimeout(r, 30));
      const res = await analyzeImageFull(canvas);
      setResult(res);
      // Vis heatmap
      if (overlayRef.current) {
        overlayRef.current.width = canvas.width;
        overlayRef.current.height = canvas.height;
        const octx = overlayRef.current.getContext("2d");
        octx.clearRect(0, 0, canvas.width, canvas.height);
        octx.drawImage(res.heatmap, 0, 0);
      }
    } catch (e) {
      toast.error("Analyse feilet: " + (e.message || e));
    } finally {
      setAnalyzing(false);
    }
  };

  const onFileInput = (e) => handleUpload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    handleUpload(e.dataTransfer.files?.[0]);
  };

  const verdict = result?.verdict;
  const level = result?.level ?? 0;

  const verdictConfig = {
    safe: {
      title: "Trygg",
      subtitle: "Sladdingen kan ikke reverseres",
      icon: <ShieldCheck className="w-8 h-8" />,
      color: "text-emerald-500",
      border: "border-emerald-500/60",
      bg: "bg-emerald-500/10",
    },
    partial: {
      title: "Delvis trygg",
      subtitle: "Mosaikk — moderat motstand",
      icon: <Shield className="w-8 h-8" />,
      color: "text-yellow-500",
      border: "border-yellow-500/60",
      bg: "bg-yellow-500/10",
    },
    mixed: {
      title: "Blandet",
      subtitle: "Deler er trygge, deler er ikke",
      icon: <ShieldAlert className="w-8 h-8" />,
      color: "text-yellow-500",
      border: "border-yellow-500/60",
      bg: "bg-yellow-500/10",
    },
    unsafe: {
      title: "Ikke trygg",
      subtitle: "AI kan reversere denne sladdingen",
      icon: <ShieldX className="w-8 h-8" />,
      color: "text-red-500",
      border: "border-red-500/60",
      bg: "bg-red-500/10",
    },
    none: {
      title: "Ingen sladding funnet",
      subtitle: "Bildet ser urørt ut",
      icon: <Info className="w-8 h-8" />,
      color: "text-[var(--cream-soft)]",
      border: "border-white/20",
      bg: "bg-white/5",
    },
  };

  const cfg = verdict ? verdictConfig[verdict] : null;

  return (
    <div className="min-h-screen bg-[var(--noir)] text-[var(--cream)]">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-6 h-6 text-[var(--magenta)]" />
            <span className="font-meta text-xs tracking-[0.2em] text-[var(--cream-soft)]">
              NORSK EROTIKK · SLADDINGS-SJEKK
            </span>
          </div>
          <h1 className="font-editorial text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Er sladdingen din <span className="text-[var(--magenta)]">trygg</span>?
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-[var(--cream-soft)]">
            Last opp et bilde du har sladdet. Vi analyserer det direkte på enheten din
            og forteller om moderne AI kan reversere sladdingen. Bildet lastes aldri opp.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {!image ? (
          <div
            data-testid="blur-check-dropzone"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-white/20 hover:border-[var(--magenta)]/60 transition p-16 text-center cursor-pointer"
            onClick={() => document.getElementById("blur-check-file").click()}
          >
            <Upload className="w-12 h-12 mx-auto mb-4 text-[var(--cream-soft)]" />
            <p className="font-editorial text-2xl mb-2">Slipp et sladdet bilde her</p>
            <p className="text-sm text-[var(--cream-soft)]">
              Eller <span className="text-[var(--magenta)] underline">velg fra enheten</span>
            </p>
            <p className="mt-6 text-xs text-[var(--cream-faint)]">
              JPG, PNG, WEBP · Analyseres lokalt · Ingen server-opplasting
            </p>
            <input
              id="blur-check-file"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFileInput}
              data-testid="blur-check-file-input"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            <div className="relative bg-black/40 border border-white/10 overflow-hidden"
                 style={{ minHeight: "300px", maxHeight: "75vh" }}>
              <canvas ref={canvasRef}
                      className="w-full h-full object-contain block"
                      style={{ maxHeight: "75vh" }} />
              {showHeatmap && (
                <canvas ref={overlayRef}
                        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                        style={{ maxHeight: "75vh" }} />
              )}
              {analyzing && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <div className="flex items-center gap-3 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin text-[var(--magenta)]" />
                    Analyserer piksler…
                  </div>
                </div>
              )}
            </div>

            <aside className="space-y-4">
              {cfg && (
                <div className={`border ${cfg.border} ${cfg.bg} p-4`}>
                  <div className={`flex items-center gap-3 mb-2 ${cfg.color}`}>
                    {cfg.icon}
                    <div>
                      <div className="font-editorial text-2xl leading-tight">{cfg.title}</div>
                      <div className="font-meta text-[10px] tracking-[0.15em] opacity-80">
                        {cfg.subtitle.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[var(--cream)] mt-3">{result.message}</p>

                  {/* Detaljer */}
                  <div className="mt-4 pt-4 border-t border-white/10 space-y-1.5 text-xs">
                    <StatRow label="Destruktive piksler" value={result.ratios.destructive} good />
                    <StatRow label="Mosaikk-piksler" value={result.ratios.mosaic} warn />
                    <StatRow label="Glatt blur (reverserbar)" value={result.ratios.soft} bad />
                    <StatRow label="Uberørt bilde" value={result.ratios.sharp} neutral />
                  </div>
                </div>
              )}

              {level < 3 && result && verdict !== "none" && (
                <Link
                  to="/veyl"
                  data-testid="blur-check-veyl-cta"
                  className="block bg-[var(--magenta)] text-white p-4 hover:opacity-90 transition"
                >
                  <div className="font-meta text-[10px] tracking-[0.2em] opacity-90 mb-1">
                    ANBEFALT NESTE STEG
                  </div>
                  <div className="font-editorial text-lg mb-1">Behandle bildet med Veyl</div>
                  <div className="text-xs opacity-90 mb-2">
                    Konturbasert destruktiv sladding som AI ikke kan reversere.
                  </div>
                  <div className="inline-flex items-center gap-1 text-xs font-meta tracking-[0.15em]">
                    ÅPNE VEYL <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              )}

              <button
                onClick={() => setShowHeatmap(!showHeatmap)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-meta tracking-[0.1em] border border-white/20 text-[var(--cream-soft)] hover:border-white/40 transition"
                data-testid="blur-check-toggle-heatmap"
              >
                {showHeatmap ? "Skjul heatmap" : "Vis heatmap"}
              </button>

              <button
                onClick={() => { setImage(null); setResult(null); }}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-meta border border-white/10 text-[var(--cream-faint)] hover:border-white/30 transition"
                data-testid="blur-check-new"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Nytt bilde
              </button>
            </aside>
          </div>
        )}

        {!image && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card
              color="emerald"
              title="Trygg sladding"
              desc="Piksel-informasjonen er permanent ødelagt. Ingen AI kan hente frem det som var. Grønn markering."
            />
            <Card
              color="yellow"
              title="Delvis trygg"
              desc="Mosaikk-pikselering. Motstår enkle verktøy, men avansert AI kan gjenskape mye. Gul markering."
            />
            <Card
              color="red"
              title="Ikke trygg"
              desc="Gaussian blur — smøres piksler ut. Gratis AI-verktøy kan hente frem ansiktet. Rød markering."
            />
          </div>
        )}
      </main>
    </div>
  );
}

function StatRow({ label, value, good, bad, warn, neutral }) {
  const pct = Math.round(value * 100);
  let color = "text-[var(--cream-soft)]";
  if (good && pct > 20) color = "text-emerald-500";
  if (bad && pct > 10) color = "text-red-500";
  if (warn && pct > 15) color = "text-yellow-500";
  if (neutral) color = "text-[var(--cream-soft)]";
  return (
    <div className="flex items-center justify-between">
      <span className="text-[var(--cream-soft)]">{label}</span>
      <span className={`font-mono ${color}`}>{pct}%</span>
    </div>
  );
}

function Card({ color, title, desc }) {
  const map = {
    emerald: "border-emerald-500/40 text-emerald-500",
    yellow: "border-yellow-500/40 text-yellow-500",
    red: "border-red-500/40 text-red-500",
  };
  return (
    <div className={`border ${map[color]} p-4`}>
      <div className="font-meta text-sm mb-1">{title}</div>
      <div className="text-xs text-[var(--cream-soft)]">{desc}</div>
    </div>
  );
}
